import { useState, useEffect } from "react";
import { Modal, Tag, Button } from "antd";
import { Landmark, Lightbulb, RotateCcw, BookOpen } from "lucide-react";
import MarkdownEditor from "../MarkdownEditor";
import { ChoiceButton, ButtonProps } from "../ChoiceButton/ChoiceButton";
import SharedTextPanel from "../SharedTextPanel/SharedTextPanel";
import SharedTextService from "../../../sdk/services/SharedTextService";
import useMediaQuery from "../../../core/hooks/useMediaQuery";
import { SharedText } from "../../../sdk/@types/Acertei";
import {
  parseEnunciado,
  TAG_TINTS,
  ScissorButton,
} from "../../features/carrossel/questionCard";

const cx = (...v: Array<string | false | null | undefined>) =>
  v.filter(Boolean).join(" ");

/**
 * Shape both call sites (question list row, register-form draft) normalize
 * to — see design.md decision 2. Neither is a `Questao.Questao` directly:
 * the register form has no populated `subject`/`testingAgency`, only ids to
 * resolve against already-loaded lists.
 */
export interface QuestionPreviewData {
  statement: string;
  choices: { description: string; correct?: boolean; rationale?: string }[];
  subjectName?: string;
  agencyName?: string;
  agencyYear?: string | number;
  tags: string[];
  disabled?: boolean;
  /** Already resolved — `Questao.view.tsx` has the full object in memory. */
  sharedText?: { id: string; title: string; body: string; source?: string };
  /** Not yet resolved — `QuestionList.view.tsx` only has the id; the modal fetches it. */
  sharedTextId?: string;
}

export interface QuestionPreviewModalProps {
  open: boolean;
  onCancel: () => void;
  data: QuestionPreviewData | null;
}

/**
 * Preview of how a question renders as its `/carrossel` card: matéria/banca/
 * ano, enunciado (via the shared `parseEnunciado` segmentation), assunto tags
 * and alternatives. Answering, eliminating alternatives and striking
 * assertion lines are all local, ephemeral state — no request, no PDF panel,
 * forum thread, report modal or action bar — see spec.md "Preview is
 * presentation-only".
 */
export default function QuestionPreviewModal({
  open,
  onCancel,
  data,
}: QuestionPreviewModalProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [eliminated, setEliminated] = useState<Set<number>>(new Set());
  const [struckLines, setStruckLines] = useState<Set<number>>(new Set());
  const [sharedTextOpen, setSharedTextOpen] = useState(false);
  const [resolvedSharedText, setResolvedSharedText] = useState<
    "idle" | "loading" | SharedText | "error"
  >("idle");
  const mobile = useMediaQuery("(max-width: 767px), (max-height: 767px)");
  const segmentos = parseEnunciado(data?.statement ?? "");

  // A lista de questões manda só o id — o modal resolve; o formulário de
  // cadastro já manda o objeto pronto (design.md decisão 2), sem fetch.
  const pendingSharedTextId =
    !data?.sharedText && data?.sharedTextId ? data.sharedTextId : undefined;

  useEffect(() => {
    if (!pendingSharedTextId) {
      // Zera ao trocar de questão: sem isto uma questão SEM texto base herdaria
      // o texto da anterior, que continuaria em `resolvedSharedText`.
      setResolvedSharedText("idle");
      return;
    }
    // `cancelled` porque a resposta de uma questão já trocada não pode pintar
    // o banner da questão aberta agora.
    let cancelled = false;
    setResolvedSharedText("loading");
    SharedTextService.get(pendingSharedTextId)
      .then((text) => {
        if (!cancelled) setResolvedSharedText(text);
      })
      .catch(() => {
        if (!cancelled) setResolvedSharedText("error");
      });
    return () => {
      cancelled = true;
    };
  }, [pendingSharedTextId]);

  const sharedText =
    data?.sharedText ??
    (typeof resolvedSharedText === "object" ? resolvedSharedText : undefined);

  const situationFor = (
    choice: QuestionPreviewData["choices"][number],
    index: number,
  ): NonNullable<ButtonProps["situation"]> => {
    if (selectedIndex === null) return "default";
    if (choice.correct) return "correct";
    if (index === selectedIndex) return "wrong";
    return "default";
  };

  const toggleEliminated = (index: number) => {
    const newEliminated = new Set(eliminated);
    const total = data?.choices.length ?? 0;
    const vaiEliminar = !newEliminated.has(index);

    // Riscar a última alternativa de pé deixaria a questão sem resposta
    // possível. A conta anterior subtraía 1 do TOTAL e ignorava as já
    // eliminadas, então com 3 alternativas dava sempre 2 e a trava nunca
    // disparava — dava para eliminar todas.
    if (vaiEliminar && total - newEliminated.size <= 1) return;

    if (newEliminated.has(index)) {
      newEliminated.delete(index);
    } else {
      newEliminated.add(index);
    }

    setEliminated(newEliminated);

    // Clear selection if the selected index was eliminated
    if (selectedIndex === index) {
      setSelectedIndex(null);
    }
  };

  const toggleStruckLine = (indice: number) => {
    const newStruckLines = new Set(struckLines);
    if (newStruckLines.has(indice)) {
      newStruckLines.delete(indice);
    } else {
      newStruckLines.add(indice);
    }
    setStruckLines(newStruckLines);
  };

  const handleReset = () => {
    setSelectedIndex(null);
    setEliminated(new Set());
    setStruckLines(new Set());
  };

  const correctChoice = data?.choices.find((choice) => choice.correct);
  const selectedChoice =
    selectedIndex !== null ? data?.choices[selectedIndex] : undefined;
  const selectedChoiceRationale =
    selectedChoice && !selectedChoice.correct
      ? selectedChoice.rationale?.trim()
      : undefined;
  const showJustificativa =
    selectedIndex !== null &&
    Boolean(correctChoice?.rationale?.trim() || selectedChoiceRationale);
  const showReset =
    selectedIndex !== null || eliminated.size > 0 || struckLines.size > 0;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={720}
      destroyOnClose
      title="Ver como no carrossel"
    >
      {data && (
        <div className="flex flex-col gap-4">
          <section className="flex flex-col gap-3.5 rounded-card border border-edge bg-surface p-5">
            <div className="flex items-center justify-between gap-2">
              <h3 className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted">
                Sobre a questão
              </h3>
              {data.disabled && <Tag color="red">DESATIVADA</Tag>}
            </div>
            <div className="flex items-start justify-between gap-3">
              <span className="shrink-0 text-[13px] text-ink-secondary">
                Matéria
              </span>
              <span
                className="truncate text-[13px] font-semibold text-ink"
                title={data.subjectName}
              >
                {data.subjectName ?? "—"}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3">
              <span className="shrink-0 text-[13px] text-ink-secondary">
                Banca / ano
              </span>
              <span className="inline-flex items-center gap-1.5 text-right text-[13px] font-semibold text-ink">
                <Landmark size={13} aria-hidden />
                {data.agencyName ?? "—"} · {data.agencyYear ?? "—"}
              </span>
            </div>
            {data.tags.length > 0 && (
              <div className="flex items-start justify-between gap-3">
                <span className="shrink-0 text-[13px] text-ink-secondary">
                  Assuntos
                </span>
                <span className="flex flex-wrap justify-end gap-1.5">
                  {data.tags.map((tag, i) => (
                    <span
                      key={tag}
                      className={cx(
                        "rounded-[5px] px-1.5 py-0.5 text-[11px] font-bold",
                        TAG_TINTS[i % TAG_TINTS.length],
                      )}
                    >
                      {tag}
                    </span>
                  ))}
                </span>
              </div>
            )}
          </section>

          {sharedText && (
            <div className="flex items-center justify-between gap-3 rounded-card border border-edge bg-surface p-4">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-brand/[0.12] text-brand">
                  <BookOpen size={16} aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="m-0 truncate text-[13px] font-semibold text-ink">
                    {sharedText.title}
                  </p>
                  <p className="m-0 text-[13px] text-ink-secondary">
                    Com base no texto, responda à questão.
                  </p>
                </div>
              </div>
              <Button
                type="primary"
                size="small"
                onClick={() => setSharedTextOpen(!sharedTextOpen)}
              >
                {sharedTextOpen ? 'Ocultar texto' : 'Abrir texto'}
              </Button>
            </div>
          )}

          <div className="enunciado-md flex flex-col gap-2 rounded-chip bg-select/[0.08] px-5 py-4">
            {segmentos.map((seg, segIndex) =>
              seg.tipo === "md" ? (
                <MarkdownEditor key={segIndex} readOnly value={seg.texto} />
              ) : (
                <div key={segIndex} className="flex items-center gap-2">
                  <div className="flex min-w-0 flex-1 items-baseline gap-2.5 rounded-[8px] px-2 py-1.5">
                    <span
                      className={cx(
                        "w-7 shrink-0 text-[15px] font-bold",
                        struckLines.has(seg.indice) ? "text-ink-muted" : "text-ink",
                      )}
                    >
                      {seg.numeral}
                    </span>
                    <span
                      className={cx(
                        "min-w-0 text-[15px] leading-relaxed",
                        struckLines.has(seg.indice)
                          ? "text-ink-muted line-through"
                          : "text-ink",
                      )}
                    >
                      {seg.texto}
                    </span>
                  </div>
                  <span hidden={selectedIndex !== null}>
                    <ScissorButton
                      cut={struckLines.has(seg.indice)}
                      onClick={() => toggleStruckLine(seg.indice)}
                      cutLabel="Riscar item"
                      restoreLabel="Restaurar item"
                      size="sm"
                      alwaysVisible
                    />
                  </span>
                </div>
              ),
            )}
          </div>

          {showReset && (
            <div className="flex justify-end">
              <Button
                type="text"
                size="small"
                icon={<RotateCcw size={14} aria-hidden />}
                onClick={handleReset}
                className="text-ink-secondary hover:text-ink"
              >
                Reiniciar
              </Button>
            </div>
          )}

          <div className="flex flex-col gap-2.5">
            {data.choices.map((choice, i) => {
              const isEliminated = eliminated.has(i);
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="min-w-0 flex-1">
                    <ChoiceButton
                      letter={String.fromCharCode(65 + i)}
                      label={choice.description ?? ""}
                      situation={situationFor(choice, i)}
                      eliminated={isEliminated}
                      onClick={isEliminated ? undefined : () => setSelectedIndex(i)}
                    />
                  </div>
                  <span hidden={selectedIndex !== null}>
                    <ScissorButton
                      cut={isEliminated}
                      onClick={() => toggleEliminated(i)}
                      cutLabel="Eliminar alternativa"
                      restoreLabel="Restaurar alternativa"
                      alwaysVisible
                    />
                  </span>
                </div>
              );
            })}
          </div>

          {showJustificativa && (
            <div className="flex flex-col gap-3 rounded-card border border-edge bg-surface-muted p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-ok/[0.12] text-ok-ink">
                  <Lightbulb size={18} aria-hidden />
                </span>
                <p className="m-0 text-[15px] font-bold text-ink">
                  Justificativa
                </p>
              </div>
              {correctChoice?.rationale?.trim() && (
                <p className="m-0 text-[15px] leading-relaxed text-ink-secondary">
                  {correctChoice.rationale}
                </p>
              )}
              {selectedChoiceRationale && (
                <p className="m-0 text-sm leading-relaxed text-ink-secondary">
                  <span className="font-semibold text-ink">
                    Sobre a alternativa {String.fromCharCode(65 + (selectedIndex as number))}:
                  </span>{" "}
                  {selectedChoiceRationale}
                </p>
              )}
            </div>
          )}

          {/* Não há um segundo "Reiniciar" aqui de propósito: o design diz que
              ele é um controle ÚNICO (question-preview-interactive-answering,
              decisão 4), e o `<Button>` de `showReset` acima já é esse. O
              merge trouxe as duas versões, com a mesma condição e o mesmo
              handler — duas apareciam juntas, e `getByRole("Reiniciar")`
              deixava de achar um só. */}
        </div>
      )}

      {sharedTextOpen && sharedText && (
        <SharedTextPanel
          text={sharedText}
          onClose={() => setSharedTextOpen(false)}
          mobile={mobile}
        />
      )}
    </Modal>
  );
}
